/**
 * home/api.ts — the browser's client for the logged-in homepage backend (GET /api/home;
 * REQUIREMENTS_HOMEPAGE.md). Reuses the generic same-origin helpers from community/api.ts (the session
 * cookie is the only credential — no token in the browser, ever) and adds the one typed call the
 * homepage loader makes. The response interfaces mirror the backend SAFE shapes exactly (db/home.ts
 * HomeData / HomeToolCard / HomeCategory).
 *
 * The /api/home route is AUTHENTICATED and ALWAYS mounted. The loader already resolves signed-in vs
 * signed-out from /api/me, so it calls this only when signed in; even so this fetch is treated as
 * NON-FATAL — any non-OK degrades to EMPTY_HOME_DATA (every module then self-hides), never a crashed
 * page (FR13/FR15 graceful degradation).
 *
 * PRIVACY / ANTI-RANKING (RT-NFR05, PC-F31): every field here is a per-tool COUNT or label — never a
 * per-person figure, never WHO adopted anything. The shapes below carry no such field, by design.
 */
import { apiGet, type ApiResult } from '../community/api';
import type { ToolEngagementCounts } from '@trailblazer/shared';

/** A governed category the FR1 tabs link into (`/tools?category=<id>`). */
export interface HomeCategory {
  id: number;
  label: string;
  /** Authorised published-tool total. Optional only while rolling API and web deployments. */
  toolCount?: number;
}

/**
 * One tool as a homepage card renders it — the SAFE public subset (mirrors db/home.ts HomeToolCard).
 *
 * `adoptionCount` is the distinct-adopter figure from `db/adoption.ts` — builder excluded, one person
 * counted once — and EVERY board now carries a real one (AE-F19). The two chronological boards used to
 * receive a hardcoded 0 that the card declined to render; the count they carry is whole-life, while
 * Trending's covers `trendingWindowDays`. Which window a card's figure covers is not discoverable from
 * this field, so the board passes it to the formatter explicitly. `datePublished` is the "added on" line.
 */
export interface HomeToolCard extends ToolEngagementCounts {
  toolId: string;
  name: string;
  description: string;
  ownerName: string;
  /** The builder's public Microsoft directory photo; absent on older responses. */
  ownerPhotoRef?: string | null;
  primaryCategoryLabel: string | null;
  adoptionCount: number;
  datePublished: string | null;
}

/**
 * One curated task strip as the homepage renders it (mirrors db/home.ts HomeStrip) — the owner's
 * task-framed heading and the tools that qualified, already in order.
 *
 * `cards` is ORDERED BY THE SERVER (RT §6.1's score, the same rule the weekly Top 5 uses) and already
 * capped to the strip's slots and filtered by the viewer's access. The client re-sorts nothing and
 * re-slices nothing: a second ordering rule here is how the board would come to disagree with the one
 * the server computed. An EMPTY `cards` is a live strip that nothing qualified for this week — the
 * section hides itself (RT-F18) rather than being dropped from the payload, so "retired" and "quiet"
 * stay distinguishable.
 */
export interface HomeStrip {
  stripId: string;
  heading: string;
  cards: HomeToolCard[];
}

/**
 * The aggregate "learning this week" figure (mirrors db/learning.ts LearningStatsThisWeek). A platform
 * STAT, never a ranking (RT-F22/PC-F31) — it names nobody. `suppressed` is the cohort-floor state
 * (fewer than the reporting minimum of distinct learners this week): it carries NO numbers, and the
 * module self-hides.
 */
export type LearningStatsThisWeek =
  { suppressed: true } | { suppressed: false; linksOpened: number; distinctLearners: number };

/** The whole homepage payload — each list self-hides on the client when empty (FR15). */
export interface HomeData {
  categories: HomeCategory[];
  recentlyAdded: HomeToolCard[];
  /** Recent eligible candidates per category, used only to complete a short Popular-now row. */
  recentFallbacks?: HomeToolCard[];
  newThisWeek: HomeToolCard[];
  trending: HomeToolCard[];
  bestOfAllTime: HomeToolCard[];
  /**
   * The trailing window, in days, that `trending[].adoptionCount` covers — echoed by the server from the
   * value it actually applied, so the board can state it (AE-F19). Not a display preference: the client
   * has no other way to know it, and a 30 written into the JSX is how the sentence outlives the window.
   */
  trendingWindowDays: number;
  /**
   * RT-F13's curated task strips, in the owner's display order. Each is an independent section; a strip
   * with no cards renders nothing, and an empty ARRAY means no strips are curated at all.
   */
  curatedStrips: HomeStrip[];
  /**
   * RT-F14 "For your department" — the one personalised board on the page, ranked by the same §6.1
   * score as a curated strip and carrying the same shape, so the same card list renders it.
   *
   * `null` is NOT the same as no cards. `null` = the board does not apply to this viewer (no directory
   * department, or one that matches no governed department row), so there is nothing to personalise on.
   * `cards: []` = it applies and nothing qualified this week. Both hide (RT-F18); only the server knows
   * which, and flattening them would leave "why has my department strip gone?" unanswerable.
   */
  departmentStrip: HomeStrip | null;
  learningThisWeek: LearningStatsThisWeek;
}

/**
 * The graceful-degrade value every caller falls back to when the fetch is not OK (FR13/FR15). Empty
 * lists everywhere ⇒ every module returns null ⇒ the page shows its greeting + category tabs + first-
 * visit state, never a broken frame.
 */
export const EMPTY_HOME_DATA: HomeData = {
  categories: [],
  recentlyAdded: [],
  recentFallbacks: [],
  newThisWeek: [],
  trending: [],
  bestOfAllTime: [],
  // Never rendered: an empty `trending` means TrendingModule returns null before it reads this. It
  // matches db/home.ts HOME_DEFAULTS.trendingWindowDays so the fallback cannot be read as a claim.
  trendingWindowDays: 30,
  curatedStrips: [],
  // `null`, not an empty strip: a failed load has personalised nothing, and claiming an empty
  // department board would assert something about the viewer this client never learned.
  departmentStrip: null,
  learningThisWeek: { suppressed: true },
};

/** GET /api/home — the homepage's dynamic modules for the viewer (live-eligible, access-filtered). */
export function getHome(): Promise<ApiResult<HomeData>> {
  return apiGet<HomeData>('/api/home');
}
